import { Link, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import { categories, products, groups } from "../data/products";
import { ui, useLang } from "../i18n/i18n";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const activeGroup = params.get("group") || "chemicals";
  const activeCategory = params.get("category") || "all";
  const { t } = useLang();

  const filtered = useMemo(() => {
    if (activeGroup === "tools-ppe") {
      return products.filter((p) => p.group === "tools-ppe");
    }
    // chemicals group
    const chems = products.filter((p) => p.group !== "tools-ppe");
    if (activeCategory === "all") return chems;
    return chems.filter((p) => Array.isArray(p.categories) && p.categories.includes(activeCategory));
  }, [activeGroup, activeCategory]);

  const setGroup = (slug) => {
    const next = {};
    if (slug && slug !== "chemicals") next.group = slug;
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setCategory = (slug) => {
    const next = {};
    if (activeGroup && activeGroup !== "chemicals") next.group = activeGroup;
    if (slug && slug !== "all") next.category = slug;
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="products-page">
      {/* Header */}
      <section className="border-b border-[#E5E7EB]" data-testid="products-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
            {t(ui.products.crumb)}
          </div>
          <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B1F3A] leading-[1.04]">
            {t(ui.products.h1)}
          </h1>
          <p className="mt-6 text-[#4B5563] text-[17px] max-w-2xl leading-relaxed">
            {t(ui.products.lead)}
          </p>
        </div>
      </section>

      {/* Group tabs */}
      <section className="border-b border-[#E5E7EB]" data-testid="group-tabs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2">
          {groups.map((g) => {
            const active = activeGroup === g.slug;
            return (
              <button
                key={g.slug}
                onClick={() => setGroup(g.slug)}
                data-testid={`group-tab-${g.slug}`}
                aria-pressed={active}
                className={`text-left p-8 md:p-10 transition-colors border-[#E5E7EB] ${
                  active ? "bg-[#0B1F3A] text-white" : "bg-white hover:bg-[#F7F8FA] text-[#0B1F3A]"
                } md:[&:nth-child(odd)]:border-r border-b md:border-b-0`}
              >
                <div
                  className={`mono text-[11px] tracking-[0.2em] uppercase ${
                    active ? "text-white/60" : "text-[#9CA3AF]"
                  }`}
                >
                  {g.code}
                </div>
                <div className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">
                  {t(g.name)}
                </div>
                <p className={`mt-3 text-sm leading-relaxed ${active ? "text-white/75" : "text-[#4B5563]"}`}>
                  {t(g.short)}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Sub-filter — only for chemicals */}
      {activeGroup === "chemicals" && (
        <section
          className="border-b border-[#E5E7EB] sticky top-16 md:top-20 z-30 bg-white/95 backdrop-blur-md"
          data-testid="category-filters"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => setCategory("all")}
              data-testid="filter-all"
              className={`shrink-0 px-4 py-2 text-sm border transition-colors ${
                activeCategory === "all"
                  ? "bg-[#0B1F3A] text-white border-[#0B1F3A]"
                  : "border-[#E5E7EB] text-[#0B1F3A] hover:border-[#0B1F3A]"
              }`}
            >
              {t(ui.products.filterAll)} · {products.filter((p) => p.group !== "tools-ppe").length}
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(c.slug)}
                data-testid={`filter-${c.slug}`}
                className={`shrink-0 px-4 py-2 text-sm border transition-colors ${
                  activeCategory === c.slug
                    ? "bg-[#0B1F3A] text-white border-[#0B1F3A]"
                    : "border-[#E5E7EB] text-[#0B1F3A] hover:border-[#0B1F3A]"
                }`}
              >
                {t(c.name)}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Active category description */}
      {activeGroup === "chemicals" && activeCategory !== "all" && (
        <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="active-category-info">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
            {categories
              .filter((c) => c.slug === activeCategory)
              .map((c) => (
                <div key={c.slug} className="grid grid-cols-12 gap-10 items-start">
                  <div className="col-span-12 md:col-span-5">
                    <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                      {t(ui.products.category)} · {c.code}
                    </div>
                    <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-[#0B1F3A] tracking-tight">
                      {t(c.name)}
                    </h2>
                  </div>
                  <p className="col-span-12 md:col-span-6 md:col-start-7 text-[#4B5563] text-[17px] leading-relaxed">
                    {t(c.long)}
                  </p>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Tools & PPE intro */}
      {activeGroup === "tools-ppe" && (
        <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="tools-info">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
            {groups
              .filter((g) => g.slug === "tools-ppe")
              .map((g) => (
                <div key={g.slug} className="grid grid-cols-12 gap-10 items-start">
                  <div className="col-span-12 md:col-span-5">
                    <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                      Group · {g.code}
                    </div>
                    <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-[#0B1F3A] tracking-tight">
                      {t(g.name)}
                    </h2>
                  </div>
                  <p className="col-span-12 md:col-span-6 md:col-start-7 text-[#4B5563] text-[17px] leading-relaxed">
                    {t(g.short)}
                  </p>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Product list */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 md:py-20">
          <div className="flex items-center justify-between mb-8">
            <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
              {filtered.length} {t(ui.common.results)}
            </div>
          </div>

          <div className="border-t border-[#E5E7EB]" data-testid="product-list">
            {filtered.map((p, i) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                data-testid={`product-row-${p.slug}`}
                className="group grid grid-cols-12 gap-4 md:gap-8 items-start md:items-center py-7 md:py-8 border-b border-[#E5E7EB] hover:bg-[#F7F8FA] transition-colors -mx-4 md:-mx-6 px-4 md:px-6"
              >
                <div className="col-span-2 md:col-span-1 mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {String(i + 1).padStart(3, "0")}
                </div>
                <div className="col-span-10 md:col-span-4">
                  <div className="text-lg md:text-xl font-semibold text-[#0B1F3A] tracking-tight">
                    {t(p.name)}
                  </div>
                  {p.synonym && <div className="mt-1 text-[13px] text-[#6B7280]">{t(p.synonym)}</div>}
                </div>
                <div className="col-span-12 md:col-span-5 text-[#4B5563] text-sm leading-relaxed line-clamp-2">
                  {t(p.summary)}
                </div>
                <div className="col-span-12 md:col-span-2 flex items-center md:justify-end gap-2 text-[#0B1F3A] mono text-[10px] tracking-[0.2em] uppercase">
                  <span>{t(ui.common.view)}</span>
                  <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
